<script lang="ts">
	import { fade } from 'svelte/transition';
	import Error from '@astlai/common/Error.svelte';
	import LoadingIndicator from '@astlai/common/LoadingIndicator.svelte';
	import IconClose from 'virtual:icons/material-symbols/right-panel-close-rounded';
	import DocumentDetail from './DocumentDetail.svelte';
	import DocumentTitle from './DocumentTitle.svelte';
	import { t } from './i18n/tranlsator.svelte';
	import SourceBadge from './SourceBadge.svelte';
	import { getDocumentContext } from 'src/contexts/documents.context.svelte';
	import { getRightPanelContext } from 'src/contexts/right-panel.context.svelte';

	interface Props {
		emptyMessage?: string;
	}
	let { emptyMessage = '' }: Props = $props();

	const rightPanelCtx = getRightPanelContext();
	const docCtx = getDocumentContext();

	const isEmpty = $derived(docCtx.documents.length === 0 && docCtx.webSources.length === 0);

	function close() {
		rightPanelCtx.close();
		docCtx.clearSelection();
	}
</script>

<div class="header">
	<span>{docCtx.selected ? t('sources.headerDetail') : t('sources.header')}</span>
	<button aria-label="Close panel" onclick={close} class="icon-button">
		<IconClose />
	</button>
</div>
<div class="body">
	{#if isEmpty}
		<div class="no-context-wrapper">
			<span class="no-context-message">{emptyMessage || t('sources.placeholder')}</span>
		</div>
	{:else if docCtx.fetchable.error}
		<div class="no-context-wrapper">
			<Error error={docCtx.fetchable.error} />
		</div>
	{:else if docCtx.fetchable.fetching}
		<div class="no-context-wrapper">
			<LoadingIndicator />
		</div>
	{:else if docCtx.selected}
		<DocumentDetail
			document={docCtx.selected?.document}
			source={docCtx.selected?.source}
			back={() => docCtx.clearSelection()}
			showBackButton={docCtx.documents.length + docCtx.webSources.length > 1}
			selectedCitationHash={docCtx.selectedCitationHash}
		/>
	{:else}
		<div class="sources">
			{#if docCtx.documents.length}
				<h4 class="section-label">{t('sources.primarySources')}</h4>
				{#each docCtx.documents as item (item.id)}
					{@const kbSource = docCtx.kbSources.find((s) => s.doc_id === item.id)}
					<button
						transition:fade
						tabindex="0"
						onclick={() => docCtx.selectByDocId(item.id)}
						class="glass context-item"
					>
						<div class="item-header">
							{#if kbSource}
								<SourceBadge
									badge={{ color: kbSource.citation_color, index: kbSource.citation_index }}
								/>
							{/if}
							<DocumentTitle doc={item} />
						</div>
					</button>
				{/each}
			{/if}

			{#if docCtx.webSources.length}
				<h4 class="section-label">{t('sources.webSources')}</h4>
				{#each docCtx.webSources as webSrc (webSrc.web_id)}
					<button
						transition:fade
						tabindex="0"
						onclick={() => docCtx.selectByWebId(webSrc.web_id!)}
						class="glass context-item"
					>
						<div class="item-header">
							<SourceBadge
								badge={{ color: webSrc.citation_color, index: webSrc.citation_index }}
								webSource
							/>
							<DocumentTitle doc={webSrc} />
						</div>
					</button>
				{/each}
			{/if}
		</div>
	{/if}
</div>

<style lang="scss">
	@use '@astlai/common/styles/mixins' as lib;

	.no-context-wrapper {
		@include lib.flex-column();
		@include lib.flex-align(center, center);
		flex: 1 1 auto;
		.no-context-message {
			color: var(--text-secondary);
			padding: 0 2rem;
			text-align: center;
		}
	}

	.header {
		@include lib.flex-row();
		@include lib.flex-align(space-between);
		gap: 0.5rem;
		padding: 1rem;
		button {
			@include lib.icon-button;
			margin: -0.5rem 0;
		}
	}

	.body {
		padding: 0 1rem 1rem 1rem;
		@include lib.flex-column();
		flex: 1 1 auto;
		overflow: hidden;
		.sources {
			@include lib.flex-column();
			gap: 0.5rem;
			padding-top: 0;
			overflow-y: auto;
			max-height: 25vh;
			@include lib.bp(gt-xs) {
				max-height: none;
			}

			.section-label {
				margin-top: 0.5rem;

				&:first-child {
					margin-top: 0;
				}
			}

			.context-item {
				flex: 0 0 auto;
				border-radius: 0.75rem;
				padding: 0.5rem;
				@include lib.flex-column();
				@include lib.flex-align(flex-start, stretch);
				gap: 0.5rem;
				overflow: hidden;
				height: auto;

				span {
					text-align: left;
				}

				.item-header {
					@include lib.flex-row();
					@include lib.flex-align();
					gap: 0.5rem;
				}
			}
		}
	}
</style>
